import React from 'react';

export type SpinnerSize = 'small' | 'medium' | 'large';

interface LoadingSpinnerProps {
  /** Optional message to display below the spinner */
  message?: string;
  /** Size of the spinner: 'small' (16px), 'medium' (24px), 'large' (48px) */
  size?: SpinnerSize;
  /** Whether to center the spinner in its container */
  centered?: boolean;
  /** Additional CSS class for customization */
  className?: string;
}

/**
 * Reusable loading spinner component
 *
 * @example
 * // Simple spinner
 * <LoadingSpinner />
 *
 * // With message
 * <LoadingSpinner message="Loading data..." />
 *
 * // Small inline spinner
 * <LoadingSpinner size="small" />
 *
 * // Large centered spinner
 * <LoadingSpinner size="large" centered message="Please wait..." />
 */
export function LoadingSpinner({
  message,
  size = 'medium',
  centered = false,
  className = ''
}: LoadingSpinnerProps) {
  const sizeClasses = {
    small: 'w-4 h-4 border-2',
    medium: 'w-6 h-6 border-[3px]',
    large: 'w-12 h-12 border-4',
  };

  const containerClass = centered
    ? `flex flex-col items-center justify-center p-8 w-full ${className}`
    : `inline-flex items-center gap-3 ${className}`;

  return (
    <div className={containerClass}>
      <div
        className={`${sizeClasses[size]} border-solid border-border border-t-primary rounded-full animate-spin`}
      />
      {message && <span className="text-[var(--color-text-muted)] italic text-sm">{message}</span>}
    </div>
  );
}

export default LoadingSpinner;
